import { MILL_1 } from "@/setup/mill-config/mill-config";
import { FeetInchesInput } from "@/shared/components/FeetInchesInput";
import { GradeSelector } from "@/shared/selectors/GradeSelector";
import { HeatSelector } from "@/shared/selectors/HeatSelector";
import { SectionSelector } from "@/shared/selectors/SectionSelector";
import { useSections } from "@/shared/selectors/SectionsOfPartSelector";
import { backgroundColor } from "@/utils/theme";
import { CheckCircleOutline, Close, Delete, Edit } from "@mui/icons-material";
import {
	Box,
	IconButton,
	Stack,
	TableCell,
	TableRow,
	TextField,
	Tooltip,
} from "@mui/material";
import { type KeyboardEvent, useEffect, useRef, useState } from "react";
import { StackerBundle, StackerBundleDraft } from "../../models/StackerBundle";
import {
	computeTotalWeight,
	dimSx,
	formatLength,
	formatWeight,
	numCellSx,
} from "../../utils/utils";
import { PiecesStepper } from "../PiecesStepper";

const QUICK_SAVE_DELAY_MS = 3000;

const blockNonIntegerKeys = (e: KeyboardEvent<HTMLInputElement>) => {
	if (["e", "E", "+", "-", ".", ","].includes(e.key)) e.preventDefault();
};

export const DisplayPiecesRow = ({
	idx,
	row,
	disabled,
	onEdit,
	onDelete,
	onQuickSave,
}: {
	idx: number;
	row: StackerBundle;
	disabled: boolean;
	onEdit: () => void;
	onDelete: () => void;
	onQuickSave: (
		bundle: StackerBundle,
		patch: {
			GoodPieces: number;
			ScrapPieces: number | null;
			TotalWeight: number | null;
		},
	) => void;
}) => {
	const goodRef = useRef(row.GoodPieces ?? 0);
	const scrapRef = useRef(row.ScrapPieces ?? 0);
	const dirtyRef = useRef(false);
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const [good, setGood] = useState(row.GoodPieces ?? 0);
	const [scrap, setScrap] = useState(row.ScrapPieces ?? 0);

	// Weight per foot for this row's section, to keep TotalWeight in sync when
	// the stepper changes Good Pieces (mirrors the edit-row auto-calc).
	const { data: sectionsData } = useSections({ assetId: MILL_1.id });
	const lbsPerFoot =
		sectionsData?.sections.find((s) => s.SectionId === row.SectionId)
			?.LBsPerFoot ?? null;

	// Total Weight is a pure frontend formula and the backend stores it verbatim,
	// so reflect Good Pieces changes live from the optimistic value; this equals
	// what will be persisted and refetched. Falls back to the stored value when
	// the section has no LBsPerFoot (nothing to recompute).
	const liveWeight =
		computeTotalWeight(lbsPerFoot, row.LengthInches, good) ??
		row.TotalWeight ??
		null;

	const scheduleSave = () => {
		if (timerRef.current) clearTimeout(timerRef.current);
		timerRef.current = setTimeout(() => {
			timerRef.current = null;
			const computedWeight = computeTotalWeight(
				lbsPerFoot,
				row.LengthInches,
				goodRef.current,
			);
			onQuickSave(row, {
				GoodPieces: goodRef.current,
				ScrapPieces: scrapRef.current === 0 ? null : scrapRef.current,
				// Recalculate from the new piece count; keep the stored value for
				// sections without an LBsPerFoot.
				TotalWeight: computedWeight ?? row.TotalWeight ?? null,
			});
		}, QUICK_SAVE_DELAY_MS);
	};

	const adjustGood = (delta: number) => {
		goodRef.current = Math.max(0, goodRef.current + delta);
		dirtyRef.current = true;
		setGood(goodRef.current);
		scheduleSave();
	};

	const adjustScrap = (delta: number) => {
		scrapRef.current = Math.max(0, scrapRef.current + delta);
		dirtyRef.current = true;
		setScrap(scrapRef.current);
		scheduleSave();
	};

	useEffect(() => {
		const serverGood = row.GoodPieces ?? 0;
		const serverScrap = row.ScrapPieces ?? 0;
		if (serverGood === goodRef.current && serverScrap === scrapRef.current) {
			dirtyRef.current = false;
		} else if (!dirtyRef.current) {
			goodRef.current = serverGood;
			scrapRef.current = serverScrap;
			setGood(serverGood);
			setScrap(serverScrap);
		}
	}, [row.GoodPieces, row.ScrapPieces]);

	useEffect(() => {
		return () => {
			if (timerRef.current) clearTimeout(timerRef.current);
		};
	}, []);

	return (
		<TableRow
			hover
			sx={{
				"&:hover .row-actions": { opacity: 1 },
				"& td": { verticalAlign: "middle" },
				"&:last-child td, &:last-child th": { border: 0 },
			}}
		>
			<TableCell sx={dimSx}>{idx}</TableCell>
			<TableCell>{row.SectionName}</TableCell>
			<TableCell sx={row.HeatName ? undefined : dimSx}>
				{row.HeatName || "-"}
			</TableCell>
			<TableCell>{row.GradeName}</TableCell>
			<TableCell sx={{ fontVariantNumeric: "tabular-nums" }}>
				{formatLength(row.LengthInches)}
			</TableCell>
			<TableCell>
				<PiecesStepper value={good} disabled={disabled} onAdjust={adjustGood} />
			</TableCell>
			<TableCell sx={{ ...(liveWeight ? {} : dimSx) }}>
				{liveWeight != null ? formatWeight(liveWeight) : "-"}
			</TableCell>
			<TableCell>
				<PiecesStepper
					value={scrap}
					disabled={disabled}
					dim={scrap === 0}
					onAdjust={adjustScrap}
				/>
			</TableCell>
			<TableCell sx={row.ScrapReason ? undefined : dimSx}>
				{row.ScrapReason || "-"}
			</TableCell>
			<TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
				<Box
					className="row-actions"
					sx={{ opacity: 0, transition: "opacity .1s" }}
				>
					<IconButton
						size="small"
						title="Edit row"
						disabled={disabled}
						onClick={onEdit}
					>
						<Edit fontSize="small" />
					</IconButton>
					<IconButton
						size="small"
						title="Delete row"
						color="error"
						disabled={disabled}
						onClick={onDelete}
					>
						<Delete fontSize="small" />
					</IconButton>
				</Box>
			</TableCell>
		</TableRow>
	);
};

export const EditPiecesRow = ({
	idx,
	draft,
	original,
	isMutating,
	onChange,
	onSave,
	onCancel,
}: {
	idx: number;
	draft: StackerBundleDraft;
	original?: StackerBundle;
	isMutating: boolean;
	onChange: (next: StackerBundleDraft) => void;
	onSave: () => void;
	onCancel: () => void;
}) => {
	const goodPieces = draft.GoodPieces ?? 0;

	// TotalWeight (tons) is derived from the section's LBsPerFoot, billet length
	// and good pieces; it is read-only and persisted with the bundle.
	const { data: sectionsData } = useSections({ assetId: MILL_1.id });
	const lbsPerFoot =
		sectionsData?.sections.find((s) => s.SectionId === draft.SectionId)
			?.LBsPerFoot ?? null;
	const calculatedTotalWeight = computeTotalWeight(
		lbsPerFoot,
		draft.LengthInches,
		goodPieces,
	);

	// By default keep the stored value from the table; only recalculate live
	// when an input that feeds the formula changes (or for a new row).
	const weightInputsChanged =
		!original ||
		original.SectionId !== draft.SectionId ||
		original.LengthInches !== draft.LengthInches ||
		(original.GoodPieces ?? 0) !== goodPieces;
	const totalWeight = weightInputsChanged
		? calculatedTotalWeight
		: (original?.TotalWeight ?? null);

	// Keep the draft's TotalWeight in sync so it is persisted on save
	useEffect(() => {
		if (draft.TotalWeight !== totalWeight) {
			onChange({ ...draft, TotalWeight: totalWeight });
		}
	}, [totalWeight]);

	const isNew = !original;
	const isDirty =
		isNew ||
		original.SectionId !== draft.SectionId ||
		original.HeatId !== draft.HeatId ||
		original.LengthInches !== draft.LengthInches ||
		(original.GoodPieces ?? 0) !== goodPieces ||
		(original.ScrapPieces ?? null) !== (draft.ScrapPieces ?? null) ||
		original.ScrapReason !== draft.ScrapReason;

	const canSave =
		isDirty &&
		draft.SectionId > 0 &&
		draft.HeatId != null &&
		draft.LengthInches > 0 &&
		goodPieces >= 0 &&
		!isMutating;

	return (
		<TableRow
			sx={{
				backgroundColor: backgroundColor,
				"& td": {
					verticalAlign: "middle",
					py: 0.75,
				},
			}}
		>
			<TableCell sx={dimSx}>{idx}</TableCell>

			<TableCell>
				<SectionSelector
					value={draft.SectionId}
					onChange={(value) => onChange({ ...draft, SectionId: value! })}
					size="small"
					sortBy="recent"
					hideTph
					assetId={MILL_1.id}
				/>
			</TableCell>

			<TableCell>
				<HeatSelector
					value={draft.HeatId}
					onChange={(value, heat) =>
						onChange({
							...draft,
							HeatId: value ?? null,
							GradeId: heat?.GradeId!,
						})
					}
					sectionId={draft.SectionId}
					assetId={MILL_1.id}
				/>
			</TableCell>

			<TableCell>
				<GradeSelector value={draft.GradeId} disabled />
			</TableCell>

			<TableCell>
				<FeetInchesInput
					value={draft.LengthInches}
					onChange={(v) => onChange({ ...draft, LengthInches: v ?? 0 })}
				/>
			</TableCell>

			<TableCell sx={numCellSx}>
				<TextField
					size="small"
					type="number"
					margin="none"
					fullWidth
					value={draft.GoodPieces ?? ""}
					onChange={(e) => {
						// Strip leading zeros (e.g. "0132" -> "132"); normalize the DOM
						// directly so it sticks even when the parsed number is unchanged.
						const digits = e.target.value
							.replace(/\D/g, "")
							.replace(/^0+(?=\d)/, "");
						e.target.value = digits;
						onChange({
							...draft,
							GoodPieces: digits === "" ? 0 : Number.parseInt(digits, 10),
						});
					}}
					onKeyDown={blockNonIntegerKeys}
					onFocus={(e) => e.target.select()}
					inputProps={{ min: 0, step: 1, style: { textAlign: "right" } }}
					sx={{ backgroundColor: "white" }}
				/>
			</TableCell>

			<TableCell sx={numCellSx}>
				<TextField
					size="small"
					margin="none"
					fullWidth
					value={formatWeight(totalWeight ?? 0)}
					disabled
					inputProps={{ style: { textAlign: "right" } }}
					sx={{ backgroundColor: "white" }}
				/>
			</TableCell>

			<TableCell sx={numCellSx}>
				<TextField
					size="small"
					type="number"
					margin="none"
					fullWidth
					value={draft.ScrapPieces ?? ""}
					onChange={(e) => {
						const digits = e.target.value
							.replace(/\D/g, "")
							.replace(/^0+(?=\d)/, "");
						e.target.value = digits;
						onChange({
							...draft,
							ScrapPieces: digits === "" ? null : Number.parseInt(digits, 10),
						});
					}}
					onKeyDown={blockNonIntegerKeys}
					onFocus={(e) => e.target.select()}
					inputProps={{ min: 0, step: 1, style: { textAlign: "right" } }}
					sx={{ backgroundColor: "white" }}
				/>
			</TableCell>

			<TableCell>
				<TextField
					size="small"
					margin="none"
					fullWidth
					value={draft.ScrapReason ?? ""}
					onChange={(e) =>
						onChange({
							...draft,
							ScrapReason: e.target.value === "" ? null : e.target.value,
						})
					}
					onFocus={(e) => e.target.select()}
					sx={{ backgroundColor: "white" }}
				/>
			</TableCell>

			<TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
				<Stack direction="row" spacing={0.5} justifyContent="flex-end">
					<Tooltip title={isDirty ? "Save changes" : "No changes to save"}>
						<Box component="span">
							<IconButton
								size="small"
								color="success"
								onClick={onSave}
								disabled={!canSave}
							>
								<CheckCircleOutline fontSize="small" />
							</IconButton>
						</Box>
					</Tooltip>
					<Tooltip title="Cancel">
						<IconButton size="small" onClick={onCancel} disabled={isMutating}>
							<Close fontSize="small" />
						</IconButton>
					</Tooltip>
				</Stack>
			</TableCell>
		</TableRow>
	);
};
